import { useState } from "react";
import Toast from "@/components/base/Toast";
import { useToast } from "@/hooks/useToast";
import { initialComments } from "@/mocks/home";

const avatarTones = [
  "bg-primary-100 text-primary-800",
  "bg-secondary-100 text-secondary-800",
  "bg-accent-100 text-accent-900",
  "bg-background-200 text-foreground-700",
];

export default function CommentSection() {
  const { message, show } = useToast();
  const [comments, setComments] = useState(initialComments);
  const [draft, setDraft] = useState("");

  const toggleLike = (id: string) => {
    setComments((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, liked: !c.liked, likes: c.liked ? c.likes - 1 : c.likes + 1 } : c,
      ),
    );
  };

  const handleSubmit = () => {
    const text = draft.trim();
    if (!text) {
      show("先写点什么再发布吧");
      return;
    }
    setComments((prev) => [
      {
        id: `cm-${Date.now()}`,
        user: "我",
        badge: "刚刚加入讨论",
        text,
        time: "刚刚",
        likes: 0,
        liked: false,
      },
      ...prev,
    ]);
    setDraft("");
    show("评论已发布（演示效果）");
  };

  return (
    <section id="comments" className="border-b border-background-200 bg-primary-950/95">
      <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <span className="font-label text-xs font-medium uppercase tracking-[0.2em] text-accent-400">
              Comments
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              热评区 · 一起蹲更新
            </h2>
            <p className="mt-3 text-sm leading-7 text-background-200">
              这里聚集了正在一起蹲更新的老粉。分享你的蹲守时间、补课心得，或者给新来的朋友指条明路。
            </p>

            <div className="mt-6 space-y-3">
              <div className="rounded-lg border border-primary-800 bg-primary-900/70 p-4">
                <div className="text-xs text-accent-400">讨论热度</div>
                <div className="mt-1 font-heading text-2xl font-bold text-background-50">1,286</div>
                <div className="mt-1 text-[11px] text-background-300">今日新增 214 条</div>
              </div>
              <div className="rounded-lg border border-primary-800 bg-primary-900/70 p-4">
                <div className="flex items-center gap-2 text-xs text-accent-400">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-verified-badge-line"></i>
                  </span>
                  老粉提示
                </div>
                <p className="mt-2 text-xs leading-6 text-background-200">
                  看到没有实锤的内容，先按「待核实」处理。等当晚汇总出来再判断，比急着转发稳妥得多。
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="rounded-lg border border-primary-800 bg-primary-900/50 p-4 md:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-sm font-bold text-primary-950">
                  我
                </span>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={200}
                  placeholder="说说你一般几点蹲更新？"
                  className="w-full rounded-md border border-primary-800 bg-primary-950/60 px-4 py-3 text-sm text-background-50 placeholder:text-background-400 focus:border-accent-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-send-plane-fill"></i>
                  </span>
                  发布
                </button>
              </div>
              <div className="mt-2 text-right text-[11px] text-background-400">
                {draft.length} / 200
              </div>
            </div>

            <ul className="mt-5 space-y-3">
              {comments.map((c, index) => (
                <li
                  key={c.id}
                  className="flex gap-3.5 rounded-lg border border-primary-800/70 bg-primary-900/40 p-4 md:p-5"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${
                      avatarTones[index % avatarTones.length]
                    }`}
                  >
                    {c.user.slice(0, 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-sm font-medium text-background-50">{c.user}</span>
                      <span className="rounded-full bg-primary-800/80 px-2 py-0.5 text-[10px] text-accent-300">
                        {c.badge}
                      </span>
                      <span className="text-[11px] text-background-400">{c.time}</span>
                    </div>
                    <p className="mt-2 text-xs leading-6 text-background-200 md:text-sm">{c.text}</p>
                    <div className="mt-3 flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => toggleLike(c.id)}
                        className={`flex cursor-pointer items-center gap-1.5 text-[11px] transition-colors ${
                          c.liked ? "text-accent-400" : "text-background-400 hover:text-accent-400"
                        }`}
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className={c.liked ? "ri-heart-fill" : "ri-heart-line"}></i>
                        </span>
                        {c.likes}
                      </button>
                      <span className="flex cursor-pointer items-center gap-1.5 text-[11px] text-background-400 hover:text-background-100">
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-chat-3-line"></i>
                        </span>
                        回复
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <Toast message={message} />
    </section>
  );
}